import React from 'react'

import { CurrencyField } from '@octane/spark'
import { Field, FieldProps, getIn } from 'formik'

type FormikAmountFieldProps = {
    name: string
    label: string
    placeholder?: string
    disabled?: boolean
    tooltip?: string | React.ReactNode
    symbol?: string
    decimalScale?: number
    align?: 'left' | 'right'
    prefilled?: boolean
    helpText?: string
    hideErrorMessage?: boolean
    showZeroAsPlaceholder?: boolean
    onChangeCallback?: (newValue: number | undefined) => void
    onBlurCallback?: (newValue: number | undefined) => void
}

function FormikAmountField({
    name,
    label,
    placeholder = '0.00',
    disabled = false,
    tooltip = '',
    symbol = '$',
    decimalScale = 2,
    align = 'right',
    prefilled = false,
    helpText = '',
    hideErrorMessage = false,
    showZeroAsPlaceholder = false,
    onChangeCallback,
    onBlurCallback,
}: Readonly<FormikAmountFieldProps>) {
    return (
        <Field name={name}>
            {({ field, form }: FieldProps) => {
                const touched = getIn(form.touched, field.name)
                const error = getIn(form.errors, field.name)
                const amountValue = getIn(form.values, field.name)

                const displayValue =
                    showZeroAsPlaceholder &&
                    (amountValue === 0 || amountValue === '0')
                        ? ''
                        : amountValue

                const displayTooltip = tooltip ? <small>{tooltip}</small> : null
                const hasError = !!(touched && error)

                const helpTextToDisplay =
                    !hideErrorMessage && hasError ? error : helpText

                return (
                    <CurrencyField
                        name={field.name}
                        id={field.name}
                        data-testid={field.name}
                        label={label}
                        disabled={disabled}
                        align={align}
                        onBlur={(event) => {
                            if (onBlurCallback) {
                                onBlurCallback(field.value || 0)
                            }

                            form.handleBlur(event)
                        }}
                        placeholder={placeholder}
                        info={displayTooltip}
                        onChange={(value) => {
                            const newValue =
                                value.formattedValue !== ''
                                    ? value.floatValue
                                    : ''

                            const event = {
                                target: {
                                    name,
                                    value: newValue,
                                },
                            }

                            form.handleChange(event)
                            if (onChangeCallback) {
                                onChangeCallback(value.floatValue)
                            }
                        }}
                        error={hasError}
                        helpText={helpTextToDisplay}
                        value={displayValue}
                        symbol={symbol}
                        decimalScale={decimalScale}
                        prefilled={prefilled}
                    />
                )
            }}
        </Field>
    )
}

export default FormikAmountField
